import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, Search, ArrowLeft, Award, Calendar, CheckCircle, XCircle, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CertificateVerify = () => {
  const navigate = useNavigate();
  const { certId: urlCertId } = useParams();
  const [certId, setCertId] = useState(urlCertId || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const verify = async (id) => {
    const searchId = id || certId;
    if (!searchId.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await fetch(`${API}/api/learning/certificate/verify/${searchId.trim()}`);
      const data = await res.json();
      setResult(data);
    } catch {
      setResult({ success: false, verified: false, message: 'Verbindungsfehler' });
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (urlCertId) verify(urlCertId);
  }, [urlCertId]);

  const catLabels = { basis: 'Pflichtschulung', pv: 'Photovoltaik', smart_home: 'Smart Home', buero: 'Büro', ki_handwerk: 'KI' };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black">
      <header className="bg-black/60 border-b border-yellow-500/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white" data-testid="verify-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-yellow-500" />
            <div>
              <h1 className="text-lg font-bold text-white">Zertifikat-Verifizierung</h1>
              <p className="text-xs text-gray-400">SmartVolt Academy - Offizieller Nachweis</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-2xl">
        <Card className="bg-gray-900/50 border-gray-800 mb-8">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Input
                placeholder="Zertifikat-ID eingeben..."
                value={certId}
                onChange={e => setCertId(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && verify()}
                className="bg-black/50 border-gray-700 text-white"
                data-testid="cert-id-input"
              />
              <Button onClick={() => verify()} disabled={loading} className="bg-yellow-500 text-black hover:bg-yellow-400 min-w-[120px]" data-testid="verify-btn">
                {loading ? <div className="animate-spin h-4 w-4 border-2 border-black border-t-transparent rounded-full" /> : <><Search className="w-4 h-4 mr-2" /> Prüfen</>}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Geben Sie die Zertifikat-ID ein oder scannen Sie den QR-Code auf dem Zertifikat.</p>
          </CardContent>
        </Card>

        {searched && result && (
          <Card className={`border-2 ${result.verified ? 'border-green-500/50 bg-green-950/20' : 'border-red-500/50 bg-red-950/20'}`} data-testid="verify-result">
            <CardContent className="pt-8 pb-8">
              <div className="text-center mb-6">
                {result.verified ? (
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3" />
                ) : (
                  <XCircle className="w-16 h-16 text-red-500 mx-auto mb-3" />
                )}
                <h2 className={`text-2xl font-bold ${result.verified ? 'text-green-400' : 'text-red-400'}`}>
                  {result.verified ? 'Zertifikat verifiziert' : 'Zertifikat nicht gefunden'}
                </h2>
              </div>

              {result.verified && result.certificate && (
                <div className="space-y-4 max-w-md mx-auto">
                  <div className="bg-black/30 p-4 rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Inhaber</span>
                      <span className="text-white font-medium">{result.certificate.holder_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Schulung</span>
                      <span className="text-white font-medium text-right text-sm">{result.certificate.course_title}</span>
                    </div>
                    {result.certificate.category && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Kategorie</span>
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 border">
                          {catLabels[result.certificate.category] || result.certificate.category}
                        </Badge>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Ausgestellt</span>
                      <span className="text-white">{result.certificate.issue_date ? new Date(result.certificate.issue_date).toLocaleDateString('de-DE') : '—'}</span>
                    </div>
                    {result.certificate.score > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Testergebnis</span>
                        <span className="text-green-400 font-bold">{result.certificate.score}%</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Status</span>
                      <Badge className={result.certificate.status === 'gueltig' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                        {result.certificate.status === 'gueltig' ? 'Gültig' : 'Abgelaufen'}
                      </Badge>
                    </div>
                  </div>

                  <div className="text-center pt-4 border-t border-gray-800">
                    <div className="flex items-center justify-center gap-2 text-gray-400 text-xs">
                      <ShieldCheck className="w-4 h-4 text-yellow-500" />
                      <span>Verifiziert durch SmartVolt Academy</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-yellow-500" />
            <p className="text-gray-400 text-sm">Diese Verifizierung ist für Handwerkskammern, Arbeitgeber und Aufsichtsbehörden zugänglich.</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/partner')} className="border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10" data-testid="partner-link">
            Partnerschaft mit SmartVolt Academy
          </Button>
        </div>
      </main>
    </div>
  );
};

export default CertificateVerify;
